import React from 'react'
import classNames from 'classnames'

import { useImageIsLoaded } from './hooks/useImageIsLoaded'

function toFaviconUrl(url: string) {
  try {
    const domain = new URL(url).hostname
    return `https://www.google.com/s2/favicons?sz=256&domain=${domain}`
  } catch {
    return `https://www.google.com/s2/favicons?sz=256&domain=${url}`
  }
}

export function Favicon({
  url,
  style = {},
  className,
  fallbackUrl,
  size = 16,
}: {
  url: string
  style?: React.CSSProperties
  className?: string
  fallbackUrl?: string
  size?: number | string
}) {
  const [currentUrl, setCurrentUrl] = React.useState(url)
  const faviconUrl = React.useMemo(() => toFaviconUrl(currentUrl), [currentUrl])

  const isImageValid = React.useCallback(async (img: HTMLImageElement) => {
    return img.naturalWidth !== 16 && img.naturalHeight !== 16
  }, [])
  const { isLoaded, isError } = useImageIsLoaded(faviconUrl, isImageValid)

  // biome-ignore lint/correctness/useExhaustiveDependencies: url triggers fallback to parent domain when original url changes
  React.useEffect(() => {
    if (!isError) {
      return
    }

    // Go back one level in case of subdomain failure, up to root domain
    setCurrentUrl((oldUrl) => {
      try {
        const urlObj = new URL(oldUrl)
        const parts = urlObj.hostname.split('.')
        if (parts.length <= 2) {
          return oldUrl // Already at root domain
        }
        parts.shift() // Remove the leftmost subdomain
        urlObj.hostname = parts.join('.')
        return urlObj.toString()
      } catch {
        return oldUrl
      }
    })
  }, [url, isError])

  return (
    <div
      style={{ height: size, width: size, ...style }}
      className={classNames('position-relative flexbox-no-shrink', className)}
    >
      <div
        className="position-absolute width-full height-full transition shade-40"
        style={{
          opacity: isLoaded || isError ? 0 : 1,
        }}
      >
        <svg
          width={size}
          height={size}
          viewBox="0 0 16 16"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M11.6791 11.6791C14.9675 8.39082 15.986 4.07791 13.954 2.04598C11.9221 0.0140485 7.60918 1.03255 4.32086 4.32086C1.03255 7.60918 0.0140481 11.9221 2.04598 13.954C4.07791 15.986 8.39082 14.9675 11.6791 11.6791Z"
            fill="currentColor"
          />
        </svg>
      </div>
      <img
        src={isError && fallbackUrl ? fallbackUrl : faviconUrl}
        onError={(e) => {
          e.stopPropagation()
          e.preventDefault()
        }}
        // eslint-disable-next-line formatjs/no-literal-string-in-jsx
        alt={`Icon for ${currentUrl}`}
        className="position-absolute block width-full height-full radius-normal transition"
        style={{
          opacity: isLoaded || isError ? 1 : 0,
        }}
      />
    </div>
  )
}
